import * as React from 'react';

import { cn } from '@/utils';

export interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'circular' | 'rounded';
  disabled?: boolean;
  loading?: boolean;
}

const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      className,
      icon: Icon,
      size = 'md',
      variant = 'rounded',
      disabled = false,
      loading = false,
      ...props
    },
    ref
  ) => {
    const sizeClasses = {
      sm: 'size-8',
      md: 'size-12',
      lg: 'size-[60px]',
      xl: 'size-20',
    };

    const variantClasses = {
      circular: 'rounded-full',
      rounded: 'rounded-2xl',
    };

    const iconSizeClasses = {
      sm: 'size-4',
      md: 'size-6',
      lg: 'size-8',
      xl: 'size-10',
    };

    return (
      <div
        ref={ref}
        className={cn(
          'relative flex items-center justify-center bg-[#cbd3ff] overflow-hidden transition-all',
          sizeClasses[size],
          variantClasses[variant],
          {
            'opacity-50 cursor-not-allowed': disabled,
            'cursor-pointer hover:scale-105': !disabled && !loading,
          },
          className
        )}
        {...props}
      >
        <Icon className={cn('text-white', iconSizeClasses[size])} />

        {loading ? (
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
            <div className="animate-spin rounded-full border-3 border-white border-t-transparent w-6 h-6" />
          </div>
        ) : null}
      </div>
    );
  }
);

Avatar.displayName = 'Avatar';

export { Avatar };
